import React, { useState } from "react";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import "./NavigationDropdownNew.scss";
import classNames from "classnames";

export interface INavigationDropdownOption {
  label: string | JSX.Element;
  icon?: IconName;
  customIcon?: React.JSX.Element;
  color?: "black" | "red";
  disabled?: boolean;
  hidden?: boolean;
  onClick: () => void;
}

export type INavigationDropdownOptionSections = Array<Array<INavigationDropdownOption>>;

export interface INavigationDropdownProps {
  showPlainTextLabel?: boolean;
  hideChevron?: boolean;
  persist?: boolean;
  leftAlign?: boolean;
  rightAlign?: boolean;
  position?: "top" | "bottom";
  label: string | JSX.Element;
  sections: INavigationDropdownOptionSections;
  informationSection?: string[];
  type?: "text" | "primary" | "secondary";
  disabled?: boolean;
  block?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Size menu to content; at least as wide as the trigger button */
  menuFitContent?: boolean;
  menuClassName?: string;
}

interface INavigationDropdownState {
  isDropdownOpen: boolean;
}

export function NavigationDropdownNew(props: INavigationDropdownProps) {
  const {
    showPlainTextLabel,
    hideChevron,
    leftAlign,
    rightAlign,
    position,
    label,
    persist,
    sections,
    informationSection,
    type = "primary",
    disabled,
    block,
    onOpenChange,
    menuFitContent,
    menuClassName,
  } = props;

  const [navigationDropdownState, setNavigationDropdownState] = useState<INavigationDropdownState>({
    isDropdownOpen: false,
  });

  const dropdownRef = useOnclickOutside(() => {
    setNavigationDropdownState(prev => {
      if (!prev.isDropdownOpen) {
        return prev;
      }

      onOpenChange?.(false);
      return { ...prev, isDropdownOpen: false };
    });
  });

  function handleDropdownClick(e: React.MouseEvent) {
    e.stopPropagation();
    setNavigationDropdownState(prev => {
      const nextOpen = !prev.isDropdownOpen;
      onOpenChange?.(nextOpen);
      return { ...prev, isDropdownOpen: nextOpen };
    });
  }

  function handleOptionClick(onClick: () => void, e: React.MouseEvent) {
    e.stopPropagation();
    onClick();
    if (!persist) {
      setNavigationDropdownState(prev => {
        if (!prev.isDropdownOpen) {
          return prev;
        }

        onOpenChange?.(false);
        return { ...prev, isDropdownOpen: false };
      });
    }
  }

  return (
    <div ref={dropdownRef} className="navigation-dropdown-new">
      <div
        onClick={disabled ? null : handleDropdownClick}
        className={classNames(showPlainTextLabel ? "navigation-plain-text-dropbutton" : "navigation-dropbutton", {
          "primary-button": type === "primary" && !showPlainTextLabel,
          "secondary-button": type === "secondary" && !showPlainTextLabel,
          "text-button": type === "text",
          "w-full text-center": block,
          "plain-text": showPlainTextLabel,
          disabled: disabled,
        })}
      >
        {label}
        {!showPlainTextLabel && !hideChevron && (
          <FontAwesomeIcon className="navigation-dropbutton-icon" icon={faChevronDown} />
        )}
      </div>
      <div
        style={{
          display: navigationDropdownState.isDropdownOpen && sections?.length > 0 ? "block" : "none",
          right: rightAlign ? 0 : null,
          left: leftAlign ? 0 : rightAlign ? null : "-25%",
          top: position === "top" ? null : "100%",
          bottom: position === "top" ? "100%" : null,
          marginTop: position === "top" ? null : "0.25rem",
          width: block ? "100%" : undefined,
        }}
        className={classNames(
          "navigation-dropdown-new-content",
          { "navigation-dropdown-new-content--fit-content": menuFitContent },
          menuClassName,
        )}
      >
        {sections.map((section, index) => {
          return (
            <React.Fragment key={index}>
              <div className="navigation-dropdown-new-content-section">
                {section
                  ?.filter((option: INavigationDropdownOption) => !option.hidden)
                  .map((option, optionIndex) => {
                    return (
                      <button
                        key={optionIndex}
                        onClick={e => handleOptionClick(option.onClick, e)}
                        disabled={option.disabled}
                        className="navigation-dropdown-new-content-section-option"
                        style={{
                          color: option.color,
                        }}
                      >
                        {option?.icon && (
                          <FontAwesomeIcon
                            className="navigation-dropdown-new-content-section-option-icon"
                            icon={["far", option.icon]}
                          />
                        )}

                        {option?.customIcon ? (
                          <div style={{ paddingRight: "10px" }}>{option?.customIcon}</div>
                        ) : null}
                        <div className="navigation-dropdown-new-content-section-option-label">{option.label}</div>
                      </button>
                    );
                  })}
              </div>
              {index < sections.length - 1 && <hr className="navigation-dropdown-new-content-section-divider" />}
            </React.Fragment>
          );
        })}
        {informationSection?.length > 0 && (
          <>
            {sections?.length > 0 && <hr className="navigation-dropdown-new-content-section-divider" />}

            <div className="navigation-dropdown-new-content-information-section">
              {informationSection.map((information, index) => {
                return <div key={index}>{information}</div>;
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
